import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiUser,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeeStatusBadge
  from "../common/EmployeeStatusBadge";

import {
  formatEmployeeCurrency,
  formatEmployeeDate,
  formatEmployeeLabel,
} from "../utils/employee.utils";

import "./EmployeeSalary.css";


const SalaryDetailsDrawer = ({
  open,

  salary,

  onClose,

  onPay,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    payments,
    setPayments,
  ] =
    useState([]);


  const [
    paymentLoading,
    setPaymentLoading,
  ] =
    useState(false);


  useEffect(
    () => {

      if (
        !open ||
        !salary?._id
      ) {

        return;

      }


      const loadPayments =
        async () => {

          try {

            setPaymentLoading(
              true
            );


            const response =
              await api.get(
                config.getEmployeeSalaryPayments(
                  salary._id
                )
              );


            if (
              response?.status ===
              "success"
            ) {

              setPayments(
                Array.isArray(
                  response?.data
                )
                  ?
                  response.data
                  :
                  response
                    ?.data
                    ?.payments ||
                  []
              );

            }

          } catch (
            error
          ) {

            console.error(
              "Salary payments error:",
              error
            );


            setPayments(
              []
            );

          } finally {

            setPaymentLoading(
              false
            );

          }

        };


      loadPayments();

    },
    [
      api,
      open,
      salary,
    ]
  );


  useEffect(
    () => {

      if (
        !open
      ) {

        return undefined;

      }


      const old =
        document
          .body
          .style
          .overflow;


      document
        .body
        .style
        .overflow =
        "hidden";


      return () => {

        document
          .body
          .style
          .overflow =
          old;

      };

    },
    [
      open,
    ]
  );


  if (
    !open ||
    !salary
  ) {

    return null;

  }


  const employee =
    salary?.employeeId &&
    typeof salary.employeeId ===
      "object"
      ?
      salary.employeeId
      :
      salary?.employee ||
      {};


  const basicSalary =
    Number(
      salary?.basicSalary ??
      salary
        ?.salary
        ?.basicSalary ??
      0
    );


  const allowances =
    Number(
      salary?.allowances ??
      salary
        ?.salary
        ?.allowances ??
      0
    );


  const deductions =
    Number(
      salary?.deductions ??
      salary
        ?.salary
        ?.deductions ??
      salary?.totalDeduction ??
      0
    );


  const netSalary =
    Number(
      salary?.netSalary ??
      salary
        ?.salary
        ?.netSalary ??
      salary?.payableAmount ??
      0
    );


  const paidAmount =
    Number(
      salary?.paidAmount ??
      0
    );


  const balanceAmount =
    Number(
      salary?.balanceAmount ??
      Math.max(
        netSalary -
        paidAmount,
        0
      )
    );


  const status =
    salary?.paymentStatus ||
    salary?.status ||
    (
      balanceAmount <=
      0
        ?
        "PAID"
        :
        paidAmount >
        0
          ?
          "PARTIALLY_PAID"
          :
          "UNPAID"
    );


  const month =
    Number(
      salary?.month ??
      salary?.salaryMonth ??
      0
    );


  const year =
    salary?.year ??
    salary?.salaryYear ??
    "-";


  const monthName =
    month >=
      1 &&
    month <=
      12
      ?
      new Date(
        2000,
        month -
        1
      )
        .toLocaleString(
          "en-IN",
          {
            month:
              "long",
          }
        )
      :
      "-";


  return ReactDOM
    .createPortal(

      <div className="employee-salary-drawer-root">

        <button
          type="button"
          className="employee-salary-drawer-backdrop"
          onClick={
            onClose
          }
          aria-label="Close"
        />


        <aside className="employee-salary-drawer">

          <header>

            <div>

              <span>
                PAYROLL DETAILS
              </span>

              <h2>
                Salary Details
              </h2>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
            >
              <FiX />
            </button>

          </header>


          <div className="employee-salary-drawer-body">

            <section className="employee-salary-drawer-profile">

              <span className="employee-salary-drawer-avatar">

                <FiUser />

              </span>


              <div>

                <small>
                  {
                    employee?.employeeCode ||
                    salary?.employeeCode ||
                    "-"
                  }
                </small>

                <h3>
                  {
                    employee?.name ||
                    salary?.employeeName ||
                    "-"
                  }
                </h3>

                <p>
                  {monthName} {year}
                </p>

              </div>


              <EmployeeStatusBadge
                status={
                  status
                }
              />

            </section>


            <section className="employee-salary-detail-section">

              <h4>
                Salary Calculation
              </h4>


              <div className="employee-salary-detail-grid">

                <SalaryDetail
                  label="Basic Salary"
                  value={
                    basicSalary
                  }
                />

                <SalaryDetail
                  label="Allowances"
                  value={
                    allowances
                  }
                />

                <SalaryDetail
                  label="Deductions"
                  value={
                    deductions
                  }
                />

                <SalaryDetail
                  label="Net Salary"
                  value={
                    netSalary
                  }
                  highlight
                />

                <SalaryDetail
                  label="Paid"
                  value={
                    paidAmount
                  }
                />

                <SalaryDetail
                  label="Balance"
                  value={
                    balanceAmount
                  }
                  warning={
                    balanceAmount >
                    0
                  }
                />

              </div>

            </section>


            <section className="employee-salary-detail-section">

              <h4>
                Payroll Information
              </h4>


              <div className="employee-salary-detail-info-grid">

                <div>

                  <FiClock />

                  <span>
                    Generated On
                  </span>

                  <strong>
                    {
                      formatEmployeeDate(
                        salary.createdAt
                      )
                    }
                  </strong>

                </div>


                <div>

                  <FiDollarSign />

                  <span>
                    Status
                  </span>

                  <strong>
                    {
                      formatEmployeeLabel(
                        status
                      )
                    }
                  </strong>

                </div>

              </div>

            </section>


            <section className="employee-salary-detail-section">

              <div className="employee-salary-payment-heading">

                <div>

                  <h4>
                    Payment History
                  </h4>

                  <p>
                    Payments recorded against this salary.
                  </p>

                </div>

              </div>


              {
                paymentLoading
                  ?
                  (
                    <div className="employee-salary-payment-empty">
                      Loading payments...
                    </div>
                  )
                  :
                  payments.length ===
                    0
                    ?
                    (
                      <div className="employee-salary-payment-empty">

                        <FiCreditCard />

                        <strong>
                          No payments recorded
                        </strong>

                      </div>
                    )
                    :
                    (
                      <div className="employee-salary-payment-list">

                        {
                          payments.map(
                            (
                              payment
                            ) => (

                              <article
                                key={
                                  payment._id
                                }
                              >

                                <div>

                                  <strong>
                                    {
                                      formatEmployeeCurrency(
                                        payment.amount
                                      )
                                    }
                                  </strong>

                                  <span>
                                    {
                                      formatEmployeeLabel(
                                        payment.paymentMode ||
                                        payment.mode
                                      )
                                    }
                                  </span>

                                </div>


                                <div>

                                  <span>
                                    {
                                      formatEmployeeDate(
                                        payment.paymentDate ||
                                        payment.createdAt
                                      )
                                    }
                                  </span>

                                  <small>
                                    {
                                      payment.referenceNumber ||
                                      payment.transactionNumber ||
                                      "-"
                                    }
                                  </small>

                                </div>

                              </article>

                            )
                          )
                        }

                      </div>
                    )
              }

            </section>

          </div>


          <footer>

            <button
              type="button"
              onClick={
                onClose
              }
            >
              Close
            </button>


            <button
              type="button"
              className="employee-salary-drawer-pay"
              onClick={
                () =>
                  onPay?.(
                    salary
                  )
              }
              disabled={
                [
                  "PAID",
                  "CANCELLED",
                ].includes(
                  String(
                    status
                  ).toUpperCase()
                )
              }
            >

              <FiDollarSign />

              Record Payment

            </button>

          </footer>

        </aside>

      </div>,

      document.body

    );

};


const SalaryDetail = ({
  label,
  value,
  highlight,
  warning,
}) => (

  <div
    className={[
      "employee-salary-detail-amount",

      highlight
        ?
        "highlight"
        :
        "",

      warning
        ?
        "warning"
        :
        "",
    ]
      .filter(
        Boolean
      )
      .join(
        " "
      )}
  >

    <span>
      {label}
    </span>

    <strong>
      {
        formatEmployeeCurrency(
          value
        )
      }
    </strong>

  </div>

);


export default SalaryDetailsDrawer;